import { getMimeTypeFromSignedSourceUrl } from 'shared/utils/sourceUrl';

/**
 * Sanitizes filename by removing quotes and replacing + with spaces.
 */
export function sanitizeFileName(name: string): string {
  let normalized = name.replace(/^['"]|['"]$/g, '');
  normalized = normalized.replace(/\+/g, ' ');
  return normalized;
}

/**
 * Generates a deterministic fallback filename based on hash of input string.
 */
export function generateFallbackFileName(input?: string): string {
  const base = input ? String(input) : 'document';
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    hash = (hash << 5) - hash + base.charCodeAt(i);
    hash |= 0;
  }
  const hex = (hash >>> 0).toString(16).slice(0, 8);
  return `document-${hex}`;
}

/**
 * Extension (lowercased) from URL path (ignores query string).
 */
export function getExtensionFromUrl(url: string): string {
  const [pathPart] = url.split('?');
  const ext = pathPart.split('.').pop();
  return (ext ?? '').toLowerCase();
}

/**
 * Decoded filename from last path segment. Returns undefined if segment looks like object id (e.g. 24 hex chars).
 */
export function getTitleFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  const [pathPart] = url.split('?');
  const rawName = pathPart.split('/').pop();
  if (!rawName) return undefined;
  let decodedName: string;
  try {
    decodedName = decodeURIComponent(rawName);
  } catch {
    decodedName = rawName;
  }
  if (/^[a-f0-9]{24}(?:\.[a-z0-9]+)?$/i.test(decodedName)) {
    return undefined;
  }
  return decodedName;
}

/**
 * MIME type inferred from URL query (response-content-type). Falls back to 'application/octet-stream'.
 */
export function getMimeTypeFromUrl(url: string): string {
  return getMimeTypeFromSignedSourceUrl(url);
}

export function redactUrlQueryForLog(
  url: string | undefined
): string | undefined {
  if (!url) {
    return undefined;
  }

  try {
    const parsed = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : 'https://localhost'
    );
    return parsed.pathname + (parsed.search ? '?[query-redacted]' : '');
  } catch {
    const [pathPart] = url.split('?');
    const redacted =
      pathPart + (url.includes('?') ? '?[query-redacted]' : '');
    return redacted.length > 200 ? `${redacted.slice(0, 200)}…` : redacted;
  }
}
